import {
  PaymentOption,
  RevenueCustomerType,
  RevenueCustomerTypeLabels,
  RevenueServiceTotalsRecord,
  RevenueTotalsRecord,
  RevenueWindowRecord
} from '@navio/nsdk';
import { getLabelMapFromEntries } from 'noddi-util';

export type RevenueSplit = 'status' | 'method' | 'type' | 'service';

export interface RevenueFilters {
  paymentOption: PaymentOption | null;
  customerType: RevenueCustomerType | null;
  serviceCategoryId: number | null;
}

export const PAYMENT_OPTION_LABELS: Record<PaymentOption, { name: string; hint: string }> = {
  [PaymentOption.PAY_BEFORE]: { name: 'Prepaid', hint: 'Paid at booking' },
  [PaymentOption.PAY_AFTER]: { name: 'Pay after', hint: 'Charged after the service' },
  [PaymentOption.INVOICE]: { name: 'Invoice', hint: 'Business invoice' }
};

export const CUSTOMER_TYPE_LABELS = getLabelMapFromEntries(RevenueCustomerType, RevenueCustomerTypeLabels);

export interface Totals {
  collected: number;
  awaitingPayment: number;
  overdue: number;
  projected: number;
  refunded: number;
  bookingsCompleted: number;
  carsCompleted: number;
  bookingsUpcoming: number;
  carsUpcoming: number;
  bookingsRefunded: number;
}

export const windowCells = (revenueWindow: RevenueWindowRecord, filters: RevenueFilters): RevenueTotalsRecord[] =>
  (filters.serviceCategoryId === null
    ? revenueWindow.totals
    : revenueWindow.byService.filter((cell) => cell.serviceCategoryId === filters.serviceCategoryId)
  ).filter(
    (cell) =>
      (filters.paymentOption === null || cell.paymentOption === filters.paymentOption) &&
      (filters.customerType === null || cell.customerType === filters.customerType)
  );

/** Split by service reads service cells; their booking counts overlap, so only money is summed from them. */
export const serviceCells = (revenueWindow: RevenueWindowRecord, filters: RevenueFilters) =>
  revenueWindow.byService.filter(
    (cell) =>
      (filters.serviceCategoryId === null || cell.serviceCategoryId === filters.serviceCategoryId) &&
      (filters.paymentOption === null || cell.paymentOption === filters.paymentOption) &&
      (filters.customerType === null || cell.customerType === filters.customerType)
  );

export const rankServices = (cells: RevenueServiceTotalsRecord[]) => {
  const byService = new Map<number | null, number>();
  for (const cell of cells) {
    const key = cell.serviceCategoryId;
    byService.set(key, (byService.get(key) ?? 0) + revenue(sum([cell])));
  }
  return [...byService.entries()].sort(([, a], [, b]) => b - a).map(([key]) => key);
};

export const periodIndex = (revenueWindow: RevenueWindowRecord, cell: RevenueTotalsRecord) =>
  revenueWindow.periodStarts.indexOf(cell.periodStart);

export const sum = (cells: RevenueTotalsRecord[]): Totals =>
  cells.reduce<Totals>(
    (acc, cell) => ({
      collected: acc.collected + cell.collected,
      awaitingPayment: acc.awaitingPayment + cell.awaitingPayment,
      overdue: acc.overdue + cell.overdue,
      projected: acc.projected + cell.projected,
      refunded: acc.refunded + cell.refunded,
      bookingsCompleted: acc.bookingsCompleted + cell.bookingsCompleted,
      carsCompleted: acc.carsCompleted + cell.carsCompleted,
      bookingsUpcoming: acc.bookingsUpcoming + cell.bookingsUpcoming,
      carsUpcoming: acc.carsUpcoming + cell.carsUpcoming,
      bookingsRefunded: acc.bookingsRefunded + cell.bookingsRefunded
    }),
    {
      collected: 0,
      awaitingPayment: 0,
      overdue: 0,
      projected: 0,
      refunded: 0,
      bookingsCompleted: 0,
      carsCompleted: 0,
      bookingsUpcoming: 0,
      carsUpcoming: 0,
      bookingsRefunded: 0
    }
  );

/** Collected, awaiting payment and projected: what the period is expected to bring in. */
export const revenue = (totals: Totals) => totals.collected + totals.awaitingPayment + totals.projected;

/** Revenue from work already done. */
export const earned = (totals: Totals) => totals.collected + totals.awaitingPayment;

export const refundRate = (totals: Totals) => {
  const gross = revenue(totals) + totals.refunded;
  return gross > 0 ? (totals.refunded / gross) * 100 : null;
};

export const pointsChange = (current: number | null, previous: number | null) =>
  current === null || previous === null ? null : current - previous;

export const ratio = (value: number, by: number) => (by > 0 ? value / by : 0);

/** Percent change; null when there is nothing to compare with. */
export const change = (current: number, previous: number) =>
  previous > 0 ? ((current - previous) / previous) * 100 : null;

export const serviceName = (id: number | null, names: Map<number, string>) =>
  id === null ? 'Other' : (names.get(id) ?? `Service ${id}`);
